import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useEndpoint } from 'api';
import { useSelectedStore } from 'contexts';
import { useLocale, useRequiredContext } from 'hooks';
import type { StoreStockCard, VariantStockResponse } from 'models/api';
import type { VariantId } from 'models/product';
import { is } from 'utils/helpers';

interface StockContextType {
	allStoresStock: StoreStockCard[] | undefined;
	isLoadingMainVariantStock: boolean;
	isLoadingStoresStock: boolean;
	isLoadingVariantsStock: boolean;
	isPurchasable: boolean;
	loadStoresStock: () => void;
	loadVariantsStock: () => void;
	mainVariantStock: VariantStockResponse | undefined;
	nearbyStoresStock: StoreStockCard[] | undefined;
	variantsStock: VariantStockResponse[] | undefined;
}

export const StockContext = createContext<StockContextType | undefined>(
	undefined,
);

interface Props {
	children: ReactNode;
	isSellable: boolean;
	variantId: VariantId;
	variantIds: VariantId[] | undefined;
}

export function StockContextProvider({
	children,
	isSellable,
	variantId,
	variantIds,
}: Props) {
	const { locale } = useLocale();
	const { selectedStore } = useSelectedStore();

	const { data: mainVariantStockData, isLoading: isLoadingMainVariantStock } =
		useEndpoint({
			name: 'GetVariantStock',
			queryParams: {
				variantIds: variantId,
				returnShippingUsps: 'true',
				storeId: selectedStore?.id,
			},
		});

	const mainVariantStock = useMemo(
		() =>
			mainVariantStockData?.find(
				(stockData) => stockData.variantId === variantId,
			),
		[mainVariantStockData, variantId],
	);

	// TODO: this might have a better place in some generic product context
	const isPurchasable = Boolean(
		isSellable &&
		// We want to default to an enabled purchase button if we don't have
		// any stock information.
		(mainVariantStock?.webStock?.inStock ?? true),
	);

	const [variantsStockIsActive, setIsVariantsStockActive] = useState(false);

	const { data: variantsStock, isLoading: isLoadingVariantsStock } =
		useEndpoint(
			variantsStockIsActive && is.arrayWithLength(variantIds)
				? {
						name: 'GetVariantStock',
						queryParams: {
							variantIds,
							returnShippingUsps: 'true',
							storeId: selectedStore?.id,
						},
					}
				: null,
		);

	const loadVariantsStock = useCallback(
		() => setIsVariantsStockActive(true),
		[],
	);

	const [storesStockIsActive, setStoresStockIsActive] = useState(false);

	const { data: storesStockData, isLoading: isLoadingStoresStock } =
		useEndpoint(
			storesStockIsActive
				? {
						name: 'GetVariantStockStores',
						pathParams: { variantId },
						queryParams: selectedStore
							? { storeId: selectedStore?.id, storeSorting: 'Nearby' }
							: { storeSorting: 'Alphabetical' },
					}
				: null,
		);

	const nearbyStoresStock = useMemo(() => {
		if (!selectedStore) {
			return undefined;
		}
		return storesStockData?.slice(0, 3);
	}, [selectedStore, storesStockData]);

	const allStoresStock = useMemo(
		() =>
			storesStockData?.toSorted((a, b) =>
				a.storeName.localeCompare(b.storeName, locale),
			),
		[locale, storesStockData],
	);

	const loadStoresStock = useCallback(() => setStoresStockIsActive(true), []);

	const value = useMemo(
		() => ({
			allStoresStock,
			isLoadingMainVariantStock,
			isLoadingStoresStock,
			isLoadingVariantsStock,
			isPurchasable,
			loadStoresStock,
			loadVariantsStock,
			mainVariantStock,
			nearbyStoresStock,
			variantsStock,
		}),
		[
			allStoresStock,
			isLoadingMainVariantStock,
			isLoadingStoresStock,
			isLoadingVariantsStock,
			isPurchasable,
			loadStoresStock,
			loadVariantsStock,
			mainVariantStock,
			nearbyStoresStock,
			variantsStock,
		],
	);
	return <StockContext value={value}>{children}</StockContext>;
}
StockContextProvider.displayName = 'StockContextProvider';

export function useStockContext() {
	return useRequiredContext(StockContext, 'StockContext');
}
